/* eslint-disable */
import FileSvg from 'images/v5/icons/file-04.svg';
import Button from 'layout/atoms/Button/Button';
import Loader from 'layout/atoms/Loader/Loader';
import Modal from 'layout/atoms/Modal/Modal';
import Label from 'layout/atoms/Label';
import TabsWizard from 'layout/atoms/TabsWizard/TabsWizard';
import FoldersTree from 'layout/organisms/FoldersTree/FoldersTree';
import Submenu from 'layout/organisms/Submenu/Submenu';
import WizardSteps from 'layout/templates/WizardSteps';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { closeModal, openModal } from 'store/layoutSlice';
import { fetchOrder, resetOrdersCreatorState, resetOrderVolume, selectOrdersCreatorItem, selectOrdersCreatorModelsAssigned, selectOrdersCreatorRefetchFlags, selectOrdersCreatorStatus, setOrderModelVariantsRefetch, setOrdersCreatorRefetchFlag, } from 'store/ordersCreatorSlice';
import { removeOrder, setOrdersRefetchFlag } from 'store/ordersSlice';
import { selectFoldersTree, selectRootFolder } from 'store/specificationsFoldersSlice';
import OrderVolume from './OrderVolume';
import ProductionVolume from './ProductionVolume';
import SpecificationsPicker from './SpecificationsPicker';
const getActiveTabFromPath = (pathname, path) => {
    if (pathname.startsWith(`${path}/production-volume`)) {
        return 'production-volume';
    }
    if (pathname.startsWith(`${path}/order-volume`)) {
        return 'order-volume';
    }
    return 'specifications';
};
const OrderCreator = () => {
    var _a, _b, _c, _d, _e;
    const { orderId } = useParams();
    const { t } = useTranslation(['orders', 'specification', 'common']);
    const dispatch = useDispatch();
    const path = `/orders/creator/${orderId}`;
    const navigate = useNavigate();
    const location = useLocation();
    const specificationsMatch = useMatch(`${path}/specifications/*`);
    const productionVolumeBaseMatch = useMatch(`${path}/production-volume`);
    const productionVolumeModelMatch = useMatch(`${path}/production-volume/:modelId`);
    const orderVolumeMatch = useMatch(`${path}/order-volume`);
    const [activeTab, setActiveTab] = useState(() => getActiveTabFromPath(location.pathname, path));
    const rootFolder = useSelector(selectRootFolder);
    const foldersTree = useSelector(selectFoldersTree);
    const memoFoldersTree = useMemo(() => foldersTree, [foldersTree]);
    const status = useSelector(selectOrdersCreatorStatus);
    const refetchFlags = useSelector(selectOrdersCreatorRefetchFlags);
    const orderItem = useSelector(selectOrdersCreatorItem);
    const models = (_b = (_a = orderItem === null || orderItem === void 0 ? void 0 : orderItem.models) === null || _a === void 0 ? void 0 : _a.edges) === null || _b === void 0 ? void 0 : _b.map((model) => model.node);
    const assignedModels = useSelector(selectOrdersCreatorModelsAssigned);
    const selectedSpecifications = assignedModels.length;
    const assignedModelsRef = useRef(assignedModels);
    const finishedRef = useRef(false);
    useEffect(() => {
        assignedModelsRef.current = assignedModels;
    }, [assignedModels]);
    const allModelsReady = assignedModels.length > 0 && assignedModels.every((model) => model.modelReady);
    const allComponentVariantsReady = selectedSpecifications > 0 && ((_c = orderItem === null || orderItem === void 0 ? void 0 : orderItem.allComponentVariantsReady) !== null && _c !== void 0 ? _c : false);
    useEffect(() => {
        dispatch(openModal({ name: 'orderCreator', data: { orderId } }));
        return () => {
            dispatch(closeModal({ name: 'orderCreator' }));
        };
    }, [dispatch, orderId]);
    useEffect(() => {
        if (status.fetch === 'idle' || refetchFlags.fetch) {
            dispatch(fetchOrder(orderId));
        }
    }, [dispatch, orderId, status, refetchFlags]);
    useEffect(() => {
        const activeTabPath = `${path}/${activeTab}`;
        const currentPath = location.pathname;
        if (currentPath !== activeTabPath && !currentPath.startsWith(`${activeTabPath}/`)) {
            navigate(activeTabPath);
        }
        if (activeTab === 'production-volume') {
            dispatch(setOrdersCreatorRefetchFlag(true));
            dispatch(setOrderModelVariantsRefetch());
        }
        if (activeTab === 'order-volume') {
            dispatch(resetOrderVolume());
        }
    }, [activeTab]);
    useEffect(() => {
        return () => {
            if (!finishedRef.current && assignedModelsRef.current.length === 0) {
                dispatch(removeOrder(orderId));
            }
            dispatch(resetOrdersCreatorState());
        };
    }, []);
    const tabs = [
        {
            id: 'specifications',
            icon: React.createElement(FileSvg, null),
            label: t('choose_specification'),
            completed: selectedSpecifications > 0,
        },
        {
            id: 'production-volume',
            icon: React.createElement(FileSvg, null),
            label: t('production_volume'),
            completed: allModelsReady,
        },
        {
            id: 'order-volume',
            icon: React.createElement(FileSvg, null),
            label: t('order_volume'),
            completed: allModelsReady && allComponentVariantsReady,
        },
    ];
    const defaultLinks = [
        {
            id: 'my_files',
            to: `${path}/specifications/my-files`,
            text: t('specification:my_files'),
            icon: React.createElement(FileSvg, null),
        },
    ];
    const currentTabIndex = tabs.findIndex((tab) => tab.id === activeTab);
    const isFirstStep = currentTabIndex === 0;
    const isLastStep = currentTabIndex === tabs.length - 1;
    const handlePrevious = () => {
        if (currentTabIndex > 0) {
            setActiveTab(tabs[currentTabIndex - 1].id);
        }
    };
    const handleNext = () => {
        if (currentTabIndex < tabs.length - 1) {
            setActiveTab(tabs[currentTabIndex + 1].id);
        }
    };
    const handleClose = useCallback(() => {
        dispatch(setOrdersRefetchFlag(true));
        setTimeout(() => {
            navigate('/orders');
        }, 350);
    }, [navigate, dispatch]);
    const handleFinish = () => {
        finishedRef.current = true;
        dispatch(setOrdersRefetchFlag(true));
        dispatch(openModal({ name: 'setOrderDeliveryDate', data: { orderId } }));
    };
    return (React.createElement(Modal, { name: "orderCreator", size: "fullScreen", header: React.createElement(Label, { weight: "strong" },
            React.createElement(FileSvg, null),
            t('add_order'),
            (orderItem === null || orderItem === void 0 ? void 0 : orderItem.orderId) ? ` - ${orderItem.orderId}` : ''), onClose: handleClose },
        React.createElement(WizardSteps, { navigation: React.createElement(React.Fragment, null,
                React.createElement("main", null,
                    React.createElement(TabsWizard, { activeTab: activeTab, tabs: tabs, onClick: (e) => setActiveTab(e) })),
                React.createElement("footer", null,
                    React.createElement(Button, { theme: "ghost", onClick: handlePrevious, disabled: isFirstStep }, t('common:back')),
                    React.createElement(Button, { color: "primary", onClick: isLastStep ? handleFinish : handleNext, disabled: !isLastStep && !((_d = tabs[currentTabIndex]) === null || _d === void 0 ? void 0 : _d.completed) }, isLastStep ? t('common:finish') : t('common:continue')))) },
            React.createElement("div", { style: { display: 'flex', width: '100%', alignItems: 'flex-start' } },
                activeTab !== 'order-volume' && (React.createElement("div", { style: { width: 240, flexShrink: 0 } },
                    specificationsMatch && (React.createElement(React.Fragment, null,
                        React.createElement(Submenu, { groups: [{ id: 'defaultLinks', items: defaultLinks }] }),
                        React.createElement(FoldersTree, { rootFolderId: rootFolder.rootFolderId, folders: memoFoldersTree, link: `${path}/specifications/folders` }))),
                    (productionVolumeBaseMatch || productionVolumeModelMatch) &&
                        (refetchFlags.modelsList ? (React.createElement(Loader, { size: "s", noMargin: true })) : (models && (React.createElement(Submenu, { groups: [
                                {
                                    id: 'specifications',
                                    items: models.map((model) => ({
                                        id: model.id,
                                        to: `${path}/production-volume/${model.id}`,
                                        text: model.modelSpecificationNode.fancyName || '-',
                                        icon: React.createElement(FileSvg, null),
                                        onClick: () => dispatch(setOrderModelVariantsRefetch()),
                                    })),
                                },
                            ] })))))),
                React.createElement("div", { style: { flex: 1, minWidth: 0 } },
                    specificationsMatch && React.createElement(SpecificationsPicker, null),
                    productionVolumeBaseMatch &&
                        (refetchFlags.modelsList ? (React.createElement(Loader, { size: "m" })) : (((_e = models === null || models === void 0 ? void 0 : models[0]) === null || _e === void 0 ? void 0 : _e.id) && (React.createElement(Navigate, { to: `${path}/production-volume/${models[0].id}`, replace: true })))),
                    productionVolumeModelMatch && (React.createElement(ProductionVolume, { modelId: productionVolumeModelMatch.params.modelId })),
                    orderVolumeMatch && React.createElement(OrderVolume, null))))));
};
export default memo(OrderCreator);
